/**
 * Employee dashboard – Working hours, holiday requests, allowance (scope-aligned).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import hrApi from './api';
import WorkingHoursCalendar from './components/WorkingHoursCalendar';
import HolidayRequestForm from './components/HolidayRequestForm';
import HolidayRequestList from './components/HolidayRequestList';
import OvertimeRequestForm from './components/OvertimeRequestForm';
import OvertimeRequestList from './components/OvertimeRequestList';
import { formatDisplayName, formatHHmmToAmPm, formatDecimalHours } from './utils/formatTime';

function parseHHmmToHours(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':').map(Number);
  if (parts.length < 2) return null;
  const h = parts[0] + (parts[1] || 0) / 60;
  return Number.isNaN(h) ? null : h;
}

function getBreakHours(entry) {
  if (!entry || entry.entryMode !== 'time' || !entry.startTime || !entry.endTime || entry.totalHours == null) return null;
  const startH = parseHHmmToHours(entry.startTime);
  const endH = parseHHmmToHours(entry.endTime);
  if (startH == null || endH == null || endH <= startH) return null;
  const breakH = Math.round((endH - startH - (Number(entry.totalHours) || 0)) * 100) / 100;
  return breakH > 0 ? breakH : null;
}
import './css/EmployeeDashboard.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

function toDateKey(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function endOfWeekSunday(date) {
  const start = startOfWeekMonday(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}

function endOfMonth(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
}

function addMonths(date, count) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + count);
  return d;
}

function formatDateShort(date) {
  return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function EmployeeDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [holidayRequests, setHolidayRequests] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayError, setHolidayError] = useState('');
  const [holidaySuccessMessage, setHolidaySuccessMessage] = useState('');
  const [showBalanceBreakdown, setShowBalanceBreakdown] = useState(false);
  const [allowance, setAllowance] = useState(null);
  const [allowanceYear, setAllowanceYear] = useState(new Date().getFullYear());
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  const [overtimeError, setOvertimeError] = useState('');
  const [overtimeSuccessMessage, setOvertimeSuccessMessage] = useState('');
  const [overviewWorkingEntries, setOverviewWorkingEntries] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [overviewRangePreset, setOverviewRangePreset] = useState('this_week');
  const [overviewPublicHolidays, setOverviewPublicHolidays] = useState([]);
  const [overviewWorkSettings, setOverviewWorkSettings] = useState(null);

  const fetchHolidays = () => {
    hrApi.get('/api/holidays').then((res) => setHolidayRequests(Array.isArray(res.data) ? res.data : [])).catch(() => setHolidayRequests([]));
  };
  const fetchOvertime = () => {
    hrApi.get('/api/overtime').then((res) => setOvertimeRequests(Array.isArray(res.data) ? res.data : [])).catch(() => setOvertimeRequests([]));
  };
  const fetchOverview = () => {
    setOverviewLoading(true);
    setOverviewError('');
    const today = new Date();
    const currentYear = today.getFullYear();
    const weekStart = startOfWeekMonday(today);
    const monthStart = startOfMonth(today);
    const twoMonthEnd = endOfMonth(addMonths(today, 1));
    const rangeStart = weekStart < monthStart ? weekStart : monthStart;
    Promise.all([
      hrApi.get('/api/holidays'),
      hrApi.get('/api/overtime'),
      hrApi.get('/api/holidays/allowance', { params: { year: currentYear } }),
      hrApi.get('/api/working-hours', {
        params: {
          startDate: toDateKey(rangeStart),
          endDate: toDateKey(twoMonthEnd),
        },
      }),
      hrApi.get('/api/default-working-hours'),
    ])
      .then(([holidaysRes, overtimeRes, allowanceRes, workingRes, workSettingsRes]) => {
        setHolidayRequests(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);
        setOvertimeRequests(Array.isArray(overtimeRes.data) ? overtimeRes.data : []);
        setAllowance(allowanceRes.data || null);
        setOverviewWorkingEntries(Array.isArray(workingRes.data) ? workingRes.data : []);
        setOverviewWorkSettings(workSettingsRes?.data || null);
      })
      .catch(() => {
        setOverviewError('Failed to load overview data.');
      })
      .finally(() => setOverviewLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    hrApi.get('/api/me').then((res) => { if (!cancelled) setMe(res.data); }).catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const role = String(me?.role || '').toLowerCase();
    if (!role) return;
    // Block manager accounts from accessing employee dashboard route.
    if (role === 'manager' || role === 'assistant_manager' || role === 'superadmin') {
      navigate('/manager', { replace: true });
    }
  }, [me?.role, navigate]);

  useEffect(() => {
    if (activeSection === 'overview') fetchOverview();
    if (activeSection === 'holidays') fetchHolidays();
    if (activeSection === 'overtime') fetchOvertime();
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'holidays') {
      hrApi.get('/api/holidays/allowance', { params: { year: allowanceYear } })
        .then((res) => setAllowance(res.data))
        .catch(() => setAllowance(null));
    }
  }, [activeSection, allowanceYear]);

  const overview = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const leaveStatusCounts = holidayRequests.reduce((acc, req) => {
      const status = String(req?.status || 'pending').toLowerCase();
      if (status === 'approved' || status === 'rejected' || status === 'pending') acc[status] += 1;
      return acc;
    }, { pending: 0, approved: 0, rejected: 0 });

    const annualAllowances = Array.isArray(allowance?.allowances) ? allowance.allowances : [];
    const vacation = annualAllowances.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'vacation');
    const carry = annualAllowances.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'carry-over');
    const annualTotal = (vacation?.allowanceDays || 0) + (carry?.allowanceDays || 0);
    const annualUsed = (vacation?.usedDays || 0) + (carry?.usedDays || 0);
    const annualRemaining = (vacation?.remaining || 0) + (carry?.remaining || 0);

    const overtimeStatusCounts = overtimeRequests.reduce((acc, req) => {
      const status = String(req?.status || 'pending').toLowerCase();
      if (status === 'approved' || status === 'rejected' || status === 'pending') acc[status] += 1;
      return acc;
    }, { pending: 0, approved: 0, rejected: 0 });
    const otBalance = allowance?.overtimeBalance || { total: 0, usedDays: 0, remaining: 0 };
    const approvedOvertimeDays = otBalance.remaining;

    return {
      leaveStatusCounts,
      annualTotal,
      annualUsed,
      annualRemaining,
      overtimeStatusCounts,
      overtimeBalance: otBalance,
      approvedOvertimeDays: Math.round(approvedOvertimeDays * 100) / 100,
      totalBalanceLeaves: Math.round((annualRemaining + otBalance.remaining) * 100) / 100,
    };
  }, [allowance, holidayRequests, overtimeRequests]);

  const overviewRange = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let requestedStart;
    let requestedEnd;
    let label;
    if (overviewRangePreset === 'this_month') {
      requestedStart = startOfMonth(today);
      requestedEnd = endOfMonth(today);
      label = 'This month';
    } else if (overviewRangePreset === 'next_two_weeks') {
      requestedStart = new Date(today);
      requestedEnd = new Date(today);
      requestedEnd.setDate(requestedEnd.getDate() + 13);
      requestedEnd.setHours(23, 59, 59, 999);
      label = 'Next two weeks';
    } else {
      requestedStart = startOfWeekMonday(today);
      requestedEnd = new Date(requestedStart);
      requestedEnd.setDate(requestedEnd.getDate() + 6);
      requestedEnd.setHours(23, 59, 59, 999);
      label = 'This week';
    }
    const start = startOfWeekMonday(requestedStart);
    const end = endOfWeekSunday(requestedEnd);
    return { start, end, requestedStart, requestedEnd, label };
  }, [overviewRangePreset]);

  React.useEffect(() => {
    const startYear = overviewRange.start.getFullYear();
    const endYear = overviewRange.end.getFullYear();
    const years = startYear === endYear ? [startYear] : [startYear, endYear];
    Promise.all(
      years.map((year) =>
        hrApi.get('/api/public-holidays', { params: { year } }).then((res) => (Array.isArray(res.data) ? res.data : []))
      )
    )
      .then((lists) => setOverviewPublicHolidays(lists.flat()))
      .catch(() => setOverviewPublicHolidays([]));
  }, [overviewRange.start, overviewRange.end]);

  const overviewRangeDays = React.useMemo(() => {
    const approvedLeaveByDate = {};
    holidayRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'approved')
      .forEach((r) => {
        const start = new Date(r.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(r.endDate);
        end.setHours(0, 0, 0, 0);
        const d = new Date(start);
        while (d <= end) {
          const key = toDateKey(d);
          if (!approvedLeaveByDate[key]) approvedLeaveByDate[key] = [];
          approvedLeaveByDate[key].push(r);
          d.setDate(d.getDate() + 1);
        }
      });

    const workingByDate = {};
    overviewWorkingEntries.forEach((entry) => {
      const key = toDateKey(entry.date);
      workingByDate[key] = entry;
    });
    const workingDays = Array.isArray(overviewWorkSettings?.workingDays) && overviewWorkSettings.workingDays.length > 0
      ? overviewWorkSettings.workingDays
      : [1, 2, 3, 4, 5];
    const daySchedule = overviewWorkSettings?.daySchedule && typeof overviewWorkSettings.daySchedule === 'object'
      ? overviewWorkSettings.daySchedule
      : {};

    const publicHolidayByDate = {};
    overviewPublicHolidays.forEach((ph) => {
      if (!ph?.date) return;
      const start = new Date(ph.date);
      start.setHours(0, 0, 0, 0);
      const end = ph.endDate ? new Date(ph.endDate) : new Date(ph.date);
      end.setHours(0, 0, 0, 0);
      const d = new Date(start);
      while (d <= end) {
        const key = toDateKey(d);
        publicHolidayByDate[key] = ph.name || ph.nameFr || 'Public holiday';
        d.setDate(d.getDate() + 1);
      }
    });

    const days = [];
    const cursor = new Date(overviewRange.start);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(overviewRange.end);
    end.setHours(0, 0, 0, 0);
    while (cursor <= end) {
      const key = toDateKey(cursor);
      const weekday = cursor.getDay();
      const isWeekend = weekday === 0 || weekday === 6;
      const isOffDay = !isWeekend && (!workingDays.includes(weekday) || daySchedule[String(weekday)] === 'off');
      const hasWorkedHours = !!workingByDate[key] && (Number(workingByDate[key].totalHours) || 0) > 0;
      const publicHolidayName = publicHolidayByDate[key] || null;
      const leaveCountRaw = (approvedLeaveByDate[key] || []).length;
      const inRange = cursor >= overviewRange.requestedStart && cursor <= overviewRange.requestedEnd;
      days.push({
        key,
        label: cursor.toLocaleDateString(undefined, { weekday: 'short' }),
        dateText: formatDateShort(cursor),
        inRange,
        isToday: key === toDateKey(new Date()),
        isWeekend,
        isOffDay,
        publicHolidayName,
        leaveCount: publicHolidayName ? 0 : leaveCountRaw,
        hasWorkedHours,
        workingHours: workingByDate[key] ? Math.round((Number(workingByDate[key].totalHours) || 0) * 100) / 100 : 0,
        entry: workingByDate[key] || null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [holidayRequests, overviewPublicHolidays, overviewRange.end, overviewRange.start, overviewWorkSettings, overviewWorkingEntries]);

  const pendingOverviewItems = React.useMemo(() => {
    const leaveItems = holidayRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'pending')
      .map((r) => {
        const s = new Date(r.startDate);
        const e = new Date(r.endDate);
        const same = toDateKey(s) === toDateKey(e);
        const dateLabel = same
          ? s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
        return {
          id: `leave-${r._id}`,
          kind: 'leave',
          dateLabel,
          createdAt: r.createdAt || r.updatedAt || r.startDate,
        };
      });
    const overtimeItems = overtimeRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'pending')
      .map((r) => {
        const applied = r.createdAt ? new Date(r.createdAt) : new Date();
        const dLabel = applied.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return {
          id: `ot-${r._id}`,
          kind: 'overtime',
          dateLabel: `Applied ${dLabel}`,
          createdAt: r.createdAt || r.updatedAt,
        };
      });
    return [...leaveItems, ...overtimeItems]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 8);
  }, [holidayRequests, overtimeRequests]);

  const handleHolidaySubmit = (payload) => {
    setHolidayError('');
    setHolidaySuccessMessage('');
    setHolidayLoading(true);
    const hasFiles = payload.files && payload.files.length > 0;
    const body = { startDate: payload.startDate, endDate: payload.endDate, comment: payload.comment || '', leaveTypeCode: payload.leaveTypeCode || 'annual' };
    if (payload.halfDay === 'am' || payload.halfDay === 'pm') body.halfDay = payload.halfDay;
    const req = hasFiles
      ? (() => {
          const formData = new FormData();
          formData.append('startDate', payload.startDate);
          formData.append('endDate', payload.endDate);
          formData.append('comment', payload.comment || '');
          formData.append('leaveTypeCode', payload.leaveTypeCode || 'annual');
          if (payload.halfDay === 'am' || payload.halfDay === 'pm') formData.append('halfDay', payload.halfDay);
          payload.files.forEach((f) => formData.append('attachments', f));
          return hrApi.post('/api/holidays', formData);
        })()
      : hrApi.post('/api/holidays', body);
    req
      .then(() => {
        fetchHolidays();
        setHolidaySuccessMessage('Your leave request was submitted.');
      })
      .catch((err) => setHolidayError(err?.response?.data?.message || 'Failed to submit'))
      .finally(() => setHolidayLoading(false));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
  };

  const handleOvertimeSubmit = (payload) => {
    setOvertimeError('');
    setOvertimeSuccessMessage('');
    setOvertimeLoading(true);
    hrApi.post('/api/overtime', payload)
      .then(() => {
        fetchOvertime();
        setOvertimeSuccessMessage('Your overtime request was submitted.');
      })
      .catch((err) => setOvertimeError(err?.response?.data?.message || 'Failed to submit'))
      .finally(() => setOvertimeLoading(false));
  };

  return (
    <div className="employee-dashboard">
      <nav className="hr-sidebar">
        <div className="hr-sidebar-logo">
          <span className="hr-sidebar-logo-mark" aria-label="Staff Portal">SP</span>
          <span className="hr-sidebar-logo-text">Staff Portal</span>
        </div>
        <ul className="nav flex-column">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <i className="bi bi-speedometer2" /> Overview
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeSection === 'working-hours' ? 'active' : ''}`}
              onClick={() => setActiveSection('working-hours')}
            >
              <i className="bi bi-calendar3" /> Working hours
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeSection === 'holidays' ? 'active' : ''}`}
              onClick={() => setActiveSection('holidays')}
            >
              <i className="bi bi-calendar-check" /> Holiday requests
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeSection === 'overtime' ? 'active' : ''}`}
              onClick={() => setActiveSection('overtime')}
            >
              <i className="bi bi-clock-history" /> Overtime requests
            </button>
          </li>
        </ul>
      </nav>

      <main className="hr-main">
        <div className="hr-main-topbar d-flex align-items-center justify-content-between px-3 py-2">
          <span className="hr-view-label">{formatDisplayName(me?.username) || 'User'} (Employee)</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right me-1" /> Logout
          </button>
        </div>
        <div className="hr-outlet">
          {activeSection === 'overview' && (
            <>
              <h2 className="hr-page-title">Dashboard</h2>
              <p className="text-muted small mb-4">
                Quick summary of leave, overtime, and pending requests.
              </p>
              {overviewError && <div className="alert alert-danger py-2">{overviewError}</div>}
              {overviewLoading ? (
                <div className="text-muted">Loading overview...</div>
              ) : (
                <>
                  <div className="employee-overview-section">
                    <div className="db-kpis db-kpis--five mb-4">
                      <div className="db-kpi">
                        <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-calendar2-week" /></div>
                        <div className="db-kpi-body">
                          <div className="db-kpi-val">{overview.annualTotal}</div>
                          <div className="db-kpi-label">Annual leaves</div>
                        </div>
                      </div>
                      <div className="db-kpi">
                        <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-check2-square" /></div>
                        <div className="db-kpi-body">
                          <div className="db-kpi-val">{overview.annualUsed}</div>
                          <div className="db-kpi-label">Used annual</div>
                        </div>
                      </div>
                      <div className="db-kpi">
                        <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-hourglass-split" /></div>
                        <div className="db-kpi-body">
                          <div className="db-kpi-val">{overview.annualRemaining}</div>
                          <div className="db-kpi-label">Annual left</div>
                        </div>
                      </div>
                      <div className="db-kpi">
                        <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-clock-history" /></div>
                        <div className="db-kpi-body">
                          <div className="db-kpi-val">{overview.approvedOvertimeDays}</div>
                          <div className="db-kpi-label">Overtime left</div>
                        </div>
                      </div>
                      <div className="db-kpi">
                        <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-calculator" /></div>
                        <div className="db-kpi-body">
                          <div className="db-kpi-val">{overview.totalBalanceLeaves}</div>
                          <div className="db-kpi-label">Total left</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="employee-overview-section">
                    <div className="db-pending db-pending--below mb-4">
                      <div className="db-pending-head">
                        <span className="db-pending-title">Pending requests</span>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setActiveSection('holidays')}>Holiday requests</button>
                          <span className="text-muted small">·</span>
                          <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setActiveSection('overtime')}>Overtime requests</button>
                        </div>
                      </div>
                      {pendingOverviewItems.length === 0 ? (
                        <p className="db-pending-empty">No pending requests</p>
                      ) : (
                        <div className="db-pending-list">
                          {pendingOverviewItems.map((item) => (
                            <div
                              key={item.id}
                              className="db-pending-item"
                              onClick={() => setActiveSection(item.kind === 'overtime' ? 'overtime' : 'holidays')}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="db-pending-item-main">
                                <div className="db-pending-item-meta">
                                  <span className="db-pending-item-name">{formatDisplayName(me?.username) || 'You'}</span>
                                  <span className={`badge rounded-pill ${item.kind === 'overtime' ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                    {item.kind === 'overtime' ? 'Overtime' : 'Leave'}
                                  </span>
                                </div>
                                <span className="db-pending-item-date">{item.dateLabel}</span>
                              </div>
                              <i className="bi bi-chevron-right db-pending-item-arrow" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="employee-overview-section">
                    <h3 className="employee-overview-section-title">Personal calendar</h3>
                    <div className="employee-range-calendar">
                      <div className="employee-range-calendar-head d-flex flex-wrap justify-content-end align-items-center gap-2 mb-1">
                        <div className="btn-group btn-group-sm" role="group" aria-label="Overview range">
                          <button
                            type="button"
                            className={`btn ${overviewRangePreset === 'this_week' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setOverviewRangePreset('this_week')}
                          >
                            This week
                          </button>
                          <button
                            type="button"
                            className={`btn ${overviewRangePreset === 'this_month' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setOverviewRangePreset('this_month')}
                          >
                            This month
                          </button>
                          <button
                            type="button"
                            className={`btn ${overviewRangePreset === 'next_two_weeks' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setOverviewRangePreset('next_two_weeks')}
                          >
                            Next 2 weeks
                          </button>
                        </div>
                      </div>
                      <div className="employee-range-subtle">
                        {overviewRange.label}: {formatDateShort(overviewRange.start)} - {formatDateShort(overviewRange.end)}
                      </div>
                      <div className="employee-range-days">
                        {overviewRangeDays.map((day) => (
                          <div
                            key={day.key}
                            className={`employee-range-day ${day.inRange ? '' : 'employee-range-day--out'} ${day.isToday ? 'employee-range-day--today' : ''} ${day.isWeekend ? 'employee-range-day--weekend' : ''} ${day.isOffDay ? 'employee-range-day--offday' : ''} ${day.publicHolidayName ? 'employee-range-day--holiday' : ''}`}
                          >
                            <div className="employee-range-day-head">
                              <span>{day.label}</span>
                              <strong>{day.dateText}</strong>
                            </div>
                            {day.publicHolidayName && <div className="employee-range-badge employee-range-badge--holiday">{day.publicHolidayName}</div>}
                            {day.leaveCount > 0 && <div className="employee-range-badge employee-range-badge--leave">Approved leave</div>}
                            {day.entry ? (
                              <div className="employee-range-worked">
                                <div>
                                  {day.entry.entryMode === 'time' && day.entry.startTime && day.entry.endTime
                                    ? `${formatHHmmToAmPm(day.entry.startTime)} – ${formatHHmmToAmPm(day.entry.endTime)}`
                                    : formatDecimalHours(day.entry.totalHours)}
                                </div>
                                {day.entry.entryMode === 'time' && (
                                  day.entry.breakStartTime && day.entry.breakEndTime
                                    ? <div className="employee-range-break">Break {formatHHmmToAmPm(day.entry.breakStartTime)} – {formatHHmmToAmPm(day.entry.breakEndTime)}</div>
                                    : getBreakHours(day.entry) != null && <div className="employee-range-break">Break {formatDecimalHours(getBreakHours(day.entry))}</div>
                                )}
                              </div>
                            ) : day.isWeekend ? (
                              <div className="employee-range-worked employee-range-worked--muted">Weekend</div>
                            ) : day.isOffDay ? (
                              <div className="employee-range-worked employee-range-worked--muted">Off day</div>
                            ) : day.publicHolidayName ? (
                              <div className="employee-range-worked employee-range-worked--muted">Public holiday</div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {activeSection === 'working-hours' && (
            <>
              <h2 className="hr-page-title">Working hours</h2>
              <WorkingHoursCalendar
                applyEmployeeSubmissionLock
                intro={
                  <p className="text-muted small mb-0">
                    Enter from/to times or hours per day.
                  </p>
                }
              />
            </>
          )}

          {(() => {
            const list = Array.isArray(allowance?.allowances) ? allowance.allowances : [];
            const vacation = list.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'vacation');
            const carry = list.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'carry-over');
            const annualSummary = {
              usedDays: Math.round(((vacation?.usedDays || 0) + (carry?.usedDays || 0)) * 100) / 100,
              remaining: Math.round(((vacation?.remaining || 0) + (carry?.remaining || 0)) * 100) / 100,
            };
            const otBalance = allowance?.overtimeBalance || { total: 0, usedDays: 0, remaining: 0 };
            const totalUsedDisplay = Math.round((annualSummary.usedDays + otBalance.usedDays) * 100) / 100;
            const totalRemainingDisplay = Math.round((annualSummary.remaining + otBalance.remaining) * 100) / 100;
            return (
              activeSection === 'holidays' && (
                <>
                  <h2 className="hr-page-title">Holiday requests</h2>
                  <div className="hr-section-card">
                    <div className="mb-4 rounded" style={{ border: '1px solid rgba(31, 78, 121, 0.15)', overflow: 'hidden' }}>
                      <div className="px-4 py-3" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)' }}>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, color: 'rgba(0,87,60,0.6)', marginBottom: 4 }}>Total leave balance</div>
                            <div className="d-flex align-items-baseline gap-2">
                              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: 'rgba(0,87,60,0.85)', lineHeight: 1 }}>{totalRemainingDisplay}</span>
                              <span className="text-muted" style={{ fontSize: '0.82rem' }}>days remaining</span>
                              <span className="text-muted" style={{ fontSize: '0.82rem' }}>·</span>
                              <span style={{ fontSize: '0.82rem', color: '#888' }}>{totalUsedDisplay} used</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1"
                            style={{ fontSize: '0.75rem', color: '#666', border: '1px solid rgba(0,0,0,0.13)', backgroundColor: 'transparent', padding: '4px 10px', borderRadius: 6 }}
                            onClick={() => setShowBalanceBreakdown((v) => !v)}
                            aria-expanded={showBalanceBreakdown}
                          >
                            {showBalanceBreakdown ? 'Hide' : 'Breakdown'}
                            <i className={`bi bi-chevron-${showBalanceBreakdown ? 'up' : 'down'}`} style={{ fontSize: '0.65rem' }} />
                          </button>
                        </div>
                      </div>
                      {showBalanceBreakdown && (
                        <div style={{ backgroundColor: '#fff', borderTop: '1px solid rgba(0,87,60,0.10)' }}>
                          {[
                            { label: 'Annual leave', used: annualSummary.usedDays, remaining: annualSummary.remaining },
                            { label: 'Overtime leave', used: otBalance.usedDays, remaining: otBalance.remaining },
                          ].map((row, i) => (
                            <div
                              key={row.label}
                              className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-4 py-2"
                              style={{ borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(0,87,60,0.35)', display: 'inline-block', flexShrink: 0 }} />
                                <span style={{ fontSize: '0.8rem', color: '#555' }}>{row.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-3">
                                <span style={{ fontSize: '0.8rem', color: '#444' }}><strong>{row.remaining}</strong> <span style={{ color: '#888' }}>remaining</span></span>
                                <span style={{ fontSize: '0.78rem', color: '#aaa' }}>{row.used} used</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <h3 className="mb-3">Submit & track requests</h3>
                    <div className="rounded p-3 mb-4" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)', border: '1px solid rgba(31, 78, 121, 0.12)' }}>
                      {holidaySuccessMessage && (
                        <div className="alert alert-success alert-dismissible py-2 mb-2" role="status" aria-live="polite">
                          <span>{holidaySuccessMessage}</span>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Dismiss"
                            onClick={() => setHolidaySuccessMessage('')}
                          />
                        </div>
                      )}
                      {holidayError && <div className="alert alert-danger py-2 mb-2">{holidayError}</div>}
                      <HolidayRequestForm onSubmit={handleHolidaySubmit} loading={holidayLoading} />
                    </div>
                    <h4 className="mb-2">Your requests</h4>
                    <HolidayRequestList requests={holidayRequests} onUpdate={fetchHolidays} />
                  </div>
                </>
              )
            );
          })()}
          {activeSection === 'overtime' && (
            <>
              <h2 className="hr-page-title">Overtime requests</h2>
              <div className="hr-section-card">
                <h3 className="mb-3">Submit & track requests</h3>
                <div className="rounded p-3 mb-4" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)', border: '1px solid rgba(31, 78, 121, 0.12)' }}>
                  {overtimeSuccessMessage && (
                    <div className="alert alert-success alert-dismissible py-2 mb-2" role="status" aria-live="polite">
                      <span>{overtimeSuccessMessage}</span>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Dismiss"
                        onClick={() => setOvertimeSuccessMessage('')}
                      />
                    </div>
                  )}
                  {overtimeError && <div className="alert alert-danger py-2 mb-2">{overtimeError}</div>}
                  <OvertimeRequestForm onSubmit={handleOvertimeSubmit} loading={overtimeLoading} />
                </div>
                <h4 className="mb-2">Your overtime requests</h4>
                <OvertimeRequestList requests={overtimeRequests} onUpdate={fetchOvertime} />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default EmployeeDashboard;
